/**
 * External store for browse folder expand state.
 * Subscribers are scoped per path so toggling one folder does not re-render the whole tree.
 */

type Listener = () => void

class BrowseExpandedStore {
  private paths = new Set<string>()
  private pathListeners = new Map<string, Set<Listener>>()
  private globalListeners = new Set<Listener>()

  isExpanded(path: string): boolean {
    return this.paths.has(path)
  }

  getPathsArray(): string[] {
    return [...this.paths]
  }

  subscribe(path: string, listener: Listener): () => void {
    let set = this.pathListeners.get(path)
    if (!set) {
      set = new Set()
      this.pathListeners.set(path, set)
    }
    set.add(listener)
    return () => {
      set!.delete(listener)
      if (set!.size === 0) this.pathListeners.delete(path)
    }
  }

  subscribeGlobal(listener: Listener): () => void {
    this.globalListeners.add(listener)
    return () => {
      this.globalListeners.delete(listener)
    }
  }

  private notifyPath(path: string) {
    this.pathListeners.get(path)?.forEach((l) => l())
    this.globalListeners.forEach((l) => l())
  }

  toggle(path: string) {
    if (this.paths.has(path)) this.paths.delete(path)
    else this.paths.add(path)
    this.notifyPath(path)
  }

  /** Add paths without removing existing (reveal-in-sidebar ancestors). */
  addPaths(paths: string[]) {
    for (const p of paths) {
      if (!this.paths.has(p)) {
        this.paths.add(p)
        this.notifyPath(p)
      }
    }
  }

  hydrate(paths: string[]) {
    const next = new Set(paths)
    const notify = new Set<string>()
    for (const p of next) {
      if (!this.paths.has(p)) notify.add(p)
    }
    for (const p of this.paths) {
      if (!next.has(p)) notify.add(p)
    }
    this.paths = next
    for (const p of notify) {
      this.pathListeners.get(p)?.forEach((l) => l())
    }
    this.globalListeners.forEach((l) => l())
  }
}

export const browseExpandedStore = new BrowseExpandedStore()
